// aifab - LogViewer v3
// Simple design: poll every 10s when visible, replace display with last 50 lines.
// No sequence tracking. No incremental appending. No sessionStorage complexity.
// For full logs: user goes to remote server using the displayed log path.

import { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../../hooks/apiFetch';

const POLL_MS  = 10000;
const TERMINAL = new Set(['COMPLETED', 'FAILED', 'FAILED_DATA', 'TIMEOUT', 'CANCELLED']);

const classify = (msg) => {
  const m = (msg || '').toLowerCase();
  if (m.includes('[init]'))                                                    return 'log-sys';
  if (m.includes('error') || m.includes('exception') || m.includes('traceback') || m.includes('failed')) return 'log-error';
  if (m.includes('warn'))                                                       return 'log-warn';
  if (m.includes('loss') || m.includes('f1') || m.includes('accuracy') || m.includes('perplexity')) return 'log-metric';
  return '';
};

const fmtMsg = (msg) => {
  try { const o = JSON.parse(msg); if (o.message) return o.message; } catch(_) {}
  return msg;
};

export function LogViewer({ jobId, status, onStatusChange }) {
  const [lines,      setLines]      = useState([]);
  const [logPath,    setLogPath]    = useState(null);
  const [serverHost, setServerHost] = useState(null);
  const [error,      setError]      = useState(null);
  const [lastPoll,   setLastPoll]   = useState(null);
  const logboxRef   = useRef(null);
  const visibleRef  = useRef(true);
  const containerRef = useRef(null);
  const isTerminal  = TERMINAL.has(status);

  // Track visibility
  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new IntersectionObserver(
      ([e]) => { visibleRef.current = e.isIntersecting; },
      { threshold: 0.05 }
    );
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  const fetchLogs = async () => {
    if (!jobId) return;
    try {
      const data = await apiFetch(`/jobs/${jobId}/logs`);
      if (data?.logs) {
        setLines(data.logs);
        setError(null);
        setLastPoll(Date.now());
      }
      if (data?.log_path)    setLogPath(data.log_path);
      if (data?.server_host) setServerHost(data.server_host);
      if (data?.status_code && TERMINAL.has(data.status_code) && onStatusChange) {
        onStatusChange(data.status_code);
      }
    } catch(e) {
      setError(e.message || 'Unable to reach server');
    }
  };

  // Poll while running and visible
  useEffect(() => {
    if (!jobId) return;
    fetchLogs(); // immediate on mount
    if (isTerminal) return;
    const t = setInterval(() => {
      if (visibleRef.current) fetchLogs();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [jobId, status]);

  // Auto-scroll to bottom when lines update
  useEffect(() => {
    if (logboxRef.current) logboxRef.current.scrollTop = logboxRef.current.scrollHeight;
  }, [lines]);

  if (!jobId) return null;

  return (
    <div ref={containerRef}>
      {/* Header */}
      <div className="flex-btw mb8">
        <span className="xs c-3" style={{ fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em' }}>
          Training Logs
          <span style={{ fontWeight:400, color:'#64748B', marginLeft:6 }}>last 50 lines</span>
        </span>
        <div className="flex gap8" style={{ alignItems:'center' }}>
          {isTerminal ? (
            <span className="xs" style={{
              color: status === 'COMPLETED' ? 'var(--green)' : 'var(--red)', fontWeight:600,
            }}>
              {status === 'COMPLETED' ? '✓ Completed' : '✗ ' + status.replace(/_/g,' ')}
            </span>
          ) : (
            <span className="xs" style={{ color:'var(--blue)', display:'flex', alignItems:'center', gap:4 }}>
              <span style={{ width:6, height:6, borderRadius:'50%', background:'var(--blue)', animation:'pulse 1.5s infinite' }}/>
              Live · refreshes every 10s
            </span>
          )}
          {lastPoll && (
            <span className="xs c-3">
              {new Date(lastPoll).toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      {/* Log box */}
      <div className="logbox" ref={logboxRef}>
        {error && <div className="log-error">[error] {error}</div>}
        {lines.length === 0 && !error && (
          <div style={{ opacity:.4 }}>Waiting for log output…</div>
        )}
        {lines.map((l, i) => (
          <div key={i} className={classify(l.message)}>
            <span style={{ opacity:.3, marginRight:8, fontSize:10, fontFamily:'var(--f-mono)' }}>
              {String(l.log_sequence ?? i).padStart(4,'0')}
            </span>
            {fmtMsg(l.message)}
          </div>
        ))}
      </div>

      {/* Remote log path */}
      {logPath && (
        <div style={{ marginTop:10, padding:'8px 12px', background:'#F1F5FB',
          border:'1px solid var(--bd)', borderRadius:3, fontSize:11 }}>
          <span style={{ color:'#64748B', marginRight:8 }}>Full log on remote server:</span>
          <code style={{ color:'#334155', fontFamily:'var(--f-mono)' }}>
            {serverHost && <span style={{ color:'#64748B' }}>{serverHost}:</span>}
            {logPath}
          </code>
          <span style={{ color:'#64748B', marginLeft:10 }}>
            · <code style={{ fontFamily:'var(--f-mono)' }}>tail -f {logPath}</code>
          </span>
        </div>
      )}
    </div>
  );
}
